import { supabase } from '../supabaseClient';
import {
  validateForfeoReserveAvailabilitySearch,
  validateForfeoReserveBookingCommand,
  type ForfeoReserveAvailabilitySearch,
  type ForfeoReserveAvailabilitySearchResult,
  type ForfeoReserveBookingCommand,
  type ForfeoReserveBookingSnapshot,
} from '../../supabase/functions/_shared/forfeo-reserve-booking';

export const FORFEO_RESERVE_BOOKING_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_BOOKING_V1' as const;

export function isForfeoReserveBookingEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_BOOKING_V1 === 'true';
}

export class ForfeoReserveBookingClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveBookingClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve-booking:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:booking:${kind}:${crypto.randomUUID()}`;
}

async function invokeBooking<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-booking-engine',
    { body },
  );
  if (error) {
    throw new ForfeoReserveBookingClientError(
      String((data as { error?: string } | null)?.error || 'booking_network_error'),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReserveBookingClientError(
      String(data?.error || 'booking_operation_failed'),
    );
  }
  return data as T;
}

export function createForfeoReserveAvailabilitySearch(
  input: Omit<ForfeoReserveAvailabilitySearch, 'correlationId'>
    & Partial<Pick<ForfeoReserveAvailabilitySearch, 'correlationId'>>,
): ForfeoReserveAvailabilitySearch {
  const query: ForfeoReserveAvailabilitySearch = {
    ...input,
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateForfeoReserveAvailabilitySearch(query);
  if (errors.length > 0) {
    throw new ForfeoReserveBookingClientError(errors.join(','));
  }
  return query;
}

export function createForfeoReserveBookingCommand(
  input: Omit<ForfeoReserveBookingCommand, 'correlationId' | 'idempotencyKey'>
    & Partial<Pick<ForfeoReserveBookingCommand, 'correlationId' | 'idempotencyKey'>>,
): ForfeoReserveBookingCommand {
  const command: ForfeoReserveBookingCommand = {
    ...input,
    correlationId: input.correlationId || correlationId(),
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
  };
  const errors = validateForfeoReserveBookingCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveBookingClientError(errors.join(','));
  }
  return command;
}

export async function searchForfeoReserveAvailability(
  query: ForfeoReserveAvailabilitySearch,
): Promise<ForfeoReserveAvailabilitySearchResult> {
  const errors = validateForfeoReserveAvailabilitySearch(query);
  if (errors.length > 0) {
    throw new ForfeoReserveBookingClientError(errors.join(','));
  }
  const data = await invokeBooking<{
    ok: true;
    availability: ForfeoReserveAvailabilitySearchResult;
  }>({ action: 'search', query });
  return data.availability;
}

export async function fetchForfeoReserveBookingSnapshot(
  businessId: string,
  startsAt: string,
  endsAt: string,
): Promise<ForfeoReserveBookingSnapshot> {
  const data = await invokeBooking<{
    ok: true;
    snapshot: ForfeoReserveBookingSnapshot;
  }>({ action: 'snapshot', businessId, startsAt, endsAt });
  return data.snapshot;
}

export async function applyForfeoReserveBookingCommand(
  command: ForfeoReserveBookingCommand,
): Promise<Record<string, unknown>> {
  const errors = validateForfeoReserveBookingCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveBookingClientError(errors.join(','));
  }
  const data = await invokeBooking<{
    ok: true;
    result: Record<string, unknown>;
  }>({ action: 'command', command });
  return data.result;
}
